import { inject, Injectable } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { Store } from '@ngrx/store';
import { map, Observable, of } from 'rxjs';
import { DateTime } from 'luxon';
import { DentalCodeDentrixInput, PatientDetailsV2, ProgressNoteV2, ProgressNoteStatus, Provider, SiteTreatmentV2 } from '@ffd/api-client';
import { AddNewTreatmentData, AddTreatmentModalService } from '@libs/charting';
import { AppointmentReason, patientActions, selectIsEaglesoftPms, selectUserProvider } from '@libs/store';
import { isNotNullOrUndefined, routes } from '@libs/utils';
import { AddTreatmentItem, mapTreatmentToTreatmentElement, RequiredInputType, Tooth } from '@libs/ui-lib';
import { mapProgressNoteToInputData } from 'libs/store/src/lib/patient/patient.utils';
import {
  CompleteAppointmentModalComponent,
  CompleteAppointmentModalComponentData,
  CompleteAppointmentModalComponentResult,
} from '../create-appointment/complete-appointment-modal/complete-appointment-modal.component';

export interface CompleteAppointmentParams {
  patient: PatientDetailsV2;
  appointmentDate: DateTime;
  procedures: AppointmentReason[];
  progressNotes?: ProgressNoteV2[];
  teeth: Tooth[];
  reopen?: boolean;
}

export interface CompleteTreatmentsParams {
  treatments: AddTreatmentItem[];
  patientId: string;
  siteId: string;
  provider?: Provider;
  appointmentDate: string;
}

interface AppointmentReasonWithProgressNote extends AppointmentReason {
  progressNote?: ProgressNoteV2;
}

const DENTRIX_INPUT_TO_REQUIRED_INPUT_TYPE: Partial<Record<DentalCodeDentrixInput, RequiredInputType>> = {
  [DentalCodeDentrixInput.SURFACE]: RequiredInputType.Surface,
  [DentalCodeDentrixInput.TOOTH]: RequiredInputType.Tooth,
  [DentalCodeDentrixInput.POST]: RequiredInputType.Tooth,
  [DentalCodeDentrixInput.ENTIRE_MOUTH]: RequiredInputType.Mouth,
  [DentalCodeDentrixInput.QUADRANT]: RequiredInputType.Quadrant,
  [DentalCodeDentrixInput.ARCH]: RequiredInputType.Arch,
};

@Injectable()
export class CompleteAppointmentService {
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);
  private readonly addTreatmentModalService = inject(AddTreatmentModalService);
  private readonly isEaglesoftPms = this.store.selectSignal(selectIsEaglesoftPms);

  /**
   * Opens the complete appointment modal with the provided data
   * @returns Observable that emits the completed treatments array or empty if cancelled/skipped
   */
  public openCompleteAppointmentModal$(params: CompleteAppointmentParams): Observable<AddTreatmentItem[] | undefined> {
    // Map procedures to treatments
    const treatments = this.mapProceduresToTreatments(params.procedures, params.progressNotes);

    if (treatments.length === 0) {
      // No treatments found for procedures - return empty array to still allow status update
      return of([]);
    }

    return this.dialog
      .open<CompleteAppointmentModalComponentResult, CompleteAppointmentModalComponentData, CompleteAppointmentModalComponent>(
        CompleteAppointmentModalComponent,
        {
          backdropClass: ['bg-overlay-light'],
          data: {
            selectedPatient: params.patient,
            appointmentDate: params.appointmentDate.toString(),
            treatments,
            teeth: params.teeth,
            reopen: params.reopen,
          },
          disableClose: true,
        },
      )
      .closed.pipe(
        map((result) => {
          if (!result) {
            // User cancelled
            return;
          }
          // Return empty array for skip or the actual treatments
          return result.completedTreatments;
        }),
      );
  }

  /**
   * Completes treatments by dispatching the onSetCompletedTreatments action
   */
  public completeTreatments(params: CompleteTreatmentsParams): void {
    const newTreatments: AddNewTreatmentData[] = params.treatments.map((treatment) => ({
      ...treatment,
      status: ProgressNoteStatus.C,
    }));

    this.store.dispatch(
      patientActions.onSetCompletedTreatments({
        newTreatments,
        isShowcase: params.siteId === routes.showcaseSiteId,
        provider: params.provider ?? this.store.selectSignal(selectUserProvider)(),
        patientId: params.patientId,
        appointmentDate: params.appointmentDate,
        walkout: this.isEaglesoftPms() || undefined,
      }),
    );
  }

  /**
   * Maps appointment procedures to treatment data objects
   */
  public mapProceduresToTreatments(procedures: AppointmentReason[], progressNotes: ProgressNoteV2[] = []): AddNewTreatmentData[] {
    const allTreatments = this.addTreatmentModalService.selectSignal((state) => state)().treatments.data ?? [];
    // Eaglesoft only walks out existing progress notes, so appointment procedures without a note are left out
    const allProcedures: AppointmentReasonWithProgressNote[] = [
      ...(this.isEaglesoftPms() ? [] : procedures),
      ...(progressNotes
        .filter((note) => note.status === ProgressNoteStatus.P)
        .map((note) => ({
          abbreviation: note.procedureAbbreviation ?? '',
          code: note.dentalCode.code,
          name: note.dentalCode.shortDescription ?? '',
          progressNoteId: note.id,
          progressNote: note,
        })) ?? []),
    ];

    return allProcedures
      .map((procedure) => this.mapProcedureToTreatment(procedure, allTreatments))
      .filter((treatment): treatment is AddNewTreatmentData => isNotNullOrUndefined(treatment));
  }

  private mapProcedureToTreatment(
    procedure: AppointmentReasonWithProgressNote,
    allTreatments: SiteTreatmentV2[],
  ): AddNewTreatmentData | undefined {
    const treatment = allTreatments.find((t) => t.dentalCode === procedure.code);
    const element = treatment ? mapTreatmentToTreatmentElement(treatment) : undefined;
    // The input type of the appointment response wins over the site treatment when it has one
    const dentrixInput = (procedure.progressNote?.dentalCode ?? procedure.dentalCode)?.dentrixInput;
    const requiredInputType = (dentrixInput && DENTRIX_INPUT_TO_REQUIRED_INPUT_TYPE[dentrixInput]) ?? element?.requiredInputType;
    const inputData =
      requiredInputType && procedure.progressNote ? mapProgressNoteToInputData(requiredInputType, procedure.progressNote) : undefined;

    return element
      ? ({
          ...(procedure.progressNote ? this.mapProgressNoteToTreatmentElement(element, procedure.progressNote) : element),
          requiredInputType,
          requiredInputData: inputData,
          status: ProgressNoteStatus.C,
          progressNoteId: procedure.progressNoteId,
        } satisfies AddNewTreatmentData)
      : undefined;
  }

  /**
   * Overrides the site treatment values with the values of the progress note from the single appointment endpoint
   */
  private mapProgressNoteToTreatmentElement(element: AddTreatmentItem, progressNote: ProgressNoteV2): AddTreatmentItem {
    const { dentalCode, procedureAbbreviation } = progressNote;

    return {
      ...element,
      description: dentalCode.shortDescription ?? element.description,
      abbreviation: procedureAbbreviation ?? element.abbreviation,
      longDescription: dentalCode.longDescription ?? element.longDescription,
      assetPath: dentalCode.appImagePath ?? element.assetPath,
      bridge: dentalCode.bridge ?? element.bridge,
      denture: dentalCode.denture ?? element.denture,
      crown: dentalCode.crown ?? element.crown,
      implant: dentalCode.implant ?? element.implant,
      treatmentMirror: dentalCode.treatmentMirror ?? element.treatmentMirror,
      removeTooth: dentalCode.removeTooth ?? element.removeTooth,
      implantAbutment: dentalCode.implantAbutment ?? element.implantAbutment,
    };
  }
}
